import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, Mail, Loader2 } from "lucide-react";
import { Reveal, MaskLine } from "../components/Reveal";
import { BeforeAfter } from "../components/BeforeAfter";
import { BRAND, SOCIALS, GALLERY, SERVICES, REMODELS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const spanClass = {
  big: "md:col-span-8 aspect-[16/10]",
  wide: "md:col-span-6 aspect-[16/11]",
  tall: "md:col-span-4 aspect-[3/4]",
};

const GalleryItem = ({ item, i }) => (
  <Reveal
    delay={(i % 3) * 0.08}
    className={`group relative overflow-hidden ${spanClass[item.span] || spanClass.wide}`}
  >
    <img
      src={item.src}
      alt={item.tag}
      loading="lazy"
      className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
        item.rotate ? "rotate-90 scale-125" : ""
      }`}
    />
    <div className="absolute inset-0 bg-obsidian/10 transition-colors duration-500 group-hover:bg-obsidian/40" />
    <div className="absolute bottom-0 left-0 flex translate-y-2 items-center gap-3 p-6 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
      <span className="h-px w-8 bg-ember" />
      <span className="text-[11px] uppercase tracking-[0.25em] text-white">{item.tag}</span>
    </div>
  </Reveal>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full border border-border bg-surface px-4 py-3.5 text-white placeholder:text-sand/60 outline-none transition-colors duration-300 focus:border-ember";

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          data-testid="contact-name"
          value={form.name}
          onChange={update("name")}
          placeholder="Name"
          className={inputClass}
        />
        <input
          data-testid="contact-email"
          type="email"
          value={form.email}
          onChange={update("email")}
          placeholder="Email"
          className={inputClass}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          data-testid="contact-phone"
          value={form.phone}
          onChange={update("phone")}
          placeholder="Phone (optional)"
          className={inputClass}
        />
        <select
          data-testid="contact-service"
          value={form.service}
          onChange={update("service")}
          className={`${inputClass} appearance-none`}
        >
          <option value="">Service of interest</option>
          {SERVICES.map((s) => (
            <option key={s.title} value={s.title} className="bg-surface">
              {s.title}
            </option>
          ))}
          <option value="Market-Ready / Realtor" className="bg-surface">
            Market-Ready / Realtor
          </option>
        </select>
      </div>
      <textarea
        data-testid="contact-message"
        value={form.message}
        onChange={update("message")}
        placeholder="Tell us about your project..."
        rows={5}
        className={`${inputClass} resize-none`}
      />
      <button
        type="submit"
        data-testid="contact-submit"
        disabled={loading}
        className="group inline-flex items-center gap-3 bg-white px-8 py-4 text-[13px] uppercase tracking-[0.18em] text-obsidian transition-colors duration-300 hover:bg-ember disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            Send inquiry
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>
    </form>
  );
};

export default function Projects() {
  return (
    <>
      {/* Header */}
      <section className="mx-auto max-w-[1500px] px-6 pb-16 pt-36 md:px-12 md:pb-24 md:pt-48">
        <p className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-ember">
          <span className="h-px w-10 bg-ember" /> Selected work
        </p>
        <h1 className="font-display text-6xl font-light leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl">
          <MaskLine delay={0.1}>Projects</MaskLine>
          <MaskLine delay={0.25}>
            <span className="italic text-sand">built to last.</span>
          </MaskLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.9 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-sand"
        >
          A look at recent hardscape, landscape, remodel and outdoor living work
          across {BRAND.location}. Every project is custom — no two backyards alike.
        </motion.p>
      </section>

      {/* Remodels — Before / After */}
      <section data-testid="remodels" className="mx-auto max-w-[1500px] px-6 pb-16 md:px-12 md:pb-24">
        <Reveal className="mb-12">
          <p className="text-[11px] uppercase tracking-[0.25em] text-ember">Remodels</p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-light leading-tight text-white sm:text-5xl">
            Inside, too — drag to see the remodel.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-10">
          {REMODELS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.1}>
              <BeforeAfter {...r} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section data-testid="gallery" className="mx-auto max-w-[1500px] px-6 pt-8 md:px-12">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {GALLERY.map((item, i) => (
            <GalleryItem key={i} item={item} i={i} />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" data-testid="contact" className="mt-24 border-t border-border md:mt-40">
        <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-16 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
          <div className="md:col-span-5">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.25em] text-ember">Start a project</p>
              <h2 className="mt-6 font-display text-4xl font-light leading-tight text-white sm:text-5xl">
                Let's talk about
                <br />
                <span className="italic text-sand">your space.</span>
              </h2>
              <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-sand">
                Free estimates on hardscape, landscaping, decks, remodels and
                market-ready projects. Tell us what you're picturing.
              </p>

              <div className="mt-10 space-y-1">
                <a
                  href={BRAND.phoneHref}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-3 py-2 text-white transition-colors hover:text-ember"
                >
                  <Phone className="h-4 w-4 text-sand" /> {BRAND.phone}
                </a>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-center gap-3 py-2 text-white transition-colors hover:text-ember break-all"
                >
                  <Mail className="h-4 w-4 shrink-0 text-sand" /> {BRAND.email}
                </a>
              </div>

              <div className="mt-8 flex gap-4">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[12px] uppercase tracking-[0.15em] text-sand transition-colors hover:text-ember"
                  >
                    {s.label}
                  </a>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
